<template>
  <div
    ref="scrollContainer"
    class="h-full min-w-0 overflow-x-hidden overflow-y-auto bg-current/5 p-2"
  >
    <div class="mx-auto max-w-full overflow-hidden" :style="documentStageStyle">
      <div
        ref="documentPages"
        class="document-pages flex w-[21cm] origin-top-left flex-col gap-4"
        :style="documentPagesStyle"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
const emit = defineEmits(["loadingComplete"])

const scrollContainer = ref<HTMLElement | null>(null)
const documentPages = ref<HTMLElement | null>(null)
const documentScale = ref(1)
const documentWidth = ref(0)
const documentHeight = ref(0)
let availableWidth = 0
let resizeObserver: ResizeObserver | null = null

const documentStageStyle = computed(() => ({
  width: `${documentWidth.value * documentScale.value}px`,
  height: `${documentHeight.value * documentScale.value}px`
}))
const documentPagesStyle = computed(() => ({
  transform: `scale(${documentScale.value})`
}))

function updateDocumentScale() {
  const container = scrollContainer.value
  const pages = documentPages.value
  if (!container || !pages) return

  documentWidth.value = Number.parseFloat(getComputedStyle(pages).width)
  documentHeight.value = pages.offsetHeight
  documentScale.value = Math.max(0, availableWidth / (documentWidth.value || 1))
}

onMounted(() => {
  // Layout changes (including asynchronously added pages) are measured directly.
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === scrollContainer.value) availableWidth = entry.contentRect.width
    }
    updateDocumentScale()
  })
  if (scrollContainer.value) resizeObserver.observe(scrollContainer.value)
  if (documentPages.value) resizeObserver.observe(documentPages.value)
  scrollContainer.value?.focus()
  emit("loadingComplete")
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})
</script>
